import { useState } from 'react';
import {
  Checkbox,
  Chip,
  ChevronDownIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  cn,
} from '@billda/design-system';
import { useActivitySites } from '../hooks/useActivitySites';

const STATUS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Booked', value: 'Booked' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' },
  { label: 'Cancelled', value: 'Cancelled' },
];

const TYPE_OPTIONS: { label: string; value: '' | 'Labour' | 'Variation' }[] = [
  { label: 'All', value: '' },
  { label: 'Labour', value: 'Labour' },
  { label: 'Variations', value: 'Variation' },
];

interface ActivityFilterContentProps {
  status: string;
  onStatusChange: (status: string) => void;
  siteIds: number[];
  onSitesChange: (ids: number[]) => void;
  type: '' | 'Labour' | 'Variation';
  onTypeChange: (type: '' | 'Labour' | 'Variation') => void;
  showStatus?: boolean;
}

function FilterChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Chip
      label={label}
      size="sm"
      variant="outlined"
      onClick={onClick}
      className={cn(
        'h-7 rounded-lg text-content-primary transition-colors duration-150',
        selected
          ? 'border-border-primary-strong bg-surface-selected font-semibold hover:bg-surface-selected'
          : 'border-border-default hover:bg-surface-raised'
      )}
    />
  );
}

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Text
        variant="caption"
        color="secondary"
        className="mb-1.5 block font-semibold"
      >
        {title}
      </Text>
      {children}
    </div>
  );
}

interface SiteMultiSelectProps {
  sites: { id: number; name: string }[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled: boolean;
  loadingLabel: string;
  testId?: string;
}

/** Multi-select over sites: a summary trigger with a checkbox list popover. */
export function SiteMultiSelect({
  sites,
  selectedIds,
  onChange,
  disabled,
  loadingLabel,
  testId = 'activity-site-filter',
}: SiteMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const summary = (() => {
    if (selectedIds.length === 0) {
      return loadingLabel;
    }
    if (selectedIds.length === 1) {
      return sites.find(s => s.id === selectedIds[0])?.name ?? '1 site';
    }
    return `${selectedIds.length} sites`;
  })();

  const toggleSite = (siteId: number) => {
    onChange(
      selectedIds.includes(siteId)
        ? selectedIds.filter(id => id !== siteId)
        : [...selectedIds, siteId]
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className="flex h-8 w-full items-center justify-between gap-2 rounded-lg border border-border-control bg-surface-default px-3 text-left type-body-md text-content-primary transition-colors hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-38"
        >
          {selectedIds.length === 0 ? (
            <span className="text-content-secondary">{summary}</span>
          ) : (
            <span>{summary}</span>
          )}
          <ChevronDownIcon
            aria-hidden="true"
            className="size-4 shrink-0 text-content-secondary"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-70 overflow-y-auto p-1">
        {sites.map(site => (
          <label
            key={site.id}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 type-body-md text-content-primary hover:bg-surface-raised"
          >
            <Checkbox
              checked={selectedIds.includes(site.id)}
              onCheckedChange={() => {
                toggleSite(site.id);
              }}
            />
            {site.name}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function ActivityFilterContent({
  status,
  onStatusChange,
  siteIds,
  onSitesChange,
  type,
  onTypeChange,
  showStatus = true,
}: ActivityFilterContentProps) {
  const { data: sites = [], isLoading: sitesLoading } = useActivitySites();
  const hasSites = sites.length > 0;
  return (
    <div className="flex flex-col gap-4 py-1">
      {showStatus && (
        <FilterSection title="Status">
          <div className="flex flex-wrap gap-1.5">
            {STATUS_OPTIONS.map(option => (
              <FilterChip
                key={option.value || 'all'}
                label={option.label}
                selected={status === option.value}
                onClick={() => {
                  onStatusChange(option.value);
                }}
              />
            ))}
          </div>
        </FilterSection>
      )}

      <FilterSection title="Type">
        <div className="flex flex-wrap gap-1.5">
          {TYPE_OPTIONS.map(option => (
            <FilterChip
              key={option.value || 'all'}
              label={option.label}
              selected={type === option.value}
              onClick={() => {
                onTypeChange(option.value);
              }}
            />
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Sites">
        <SiteMultiSelect
          sites={sites}
          selectedIds={siteIds}
          onChange={onSitesChange}
          disabled={!hasSites}
          loadingLabel={
            sitesLoading
              ? 'Loading sites…'
              : hasSites
                ? 'All sites'
                : 'No sites available'
          }
        />
      </FilterSection>
    </div>
  );
}
